import { useCallback, useEffect, useMemo, useState } from "react";
import { MediaProject } from "../../../../types/MediaProject";
import {
  listenToMediaProjects,
  deleteMediaProject,
} from "../../../../database/community/mediaProjects";

// Live view of the composed MediaProjects (carousels and reels the MCP built
// from library assets). The admin client reads and deletes only; composition
// happens through the MCP.

export type MediaProjectsState = {
  projects: MediaProject[];
  loading: boolean;
  projectById: Map<string, MediaProject>;
  remove: (project: MediaProject) => Promise<void>;
};

export const useMediaProjects = (communityId?: string): MediaProjectsState => {
  const [projects, setProjects] = useState<MediaProject[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!communityId) {
      setProjects([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsubscribe = listenToMediaProjects(
      communityId,
      (next) => {
        setProjects(next);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsubscribe();
  }, [communityId]);

  const remove = useCallback(
    async (project: MediaProject) => {
      if (!communityId) return;
      setProjects((prev) => prev.filter((p) => p.id !== project.id));
      try {
        await deleteMediaProject(communityId, project.id);
      } catch (e) {
        console.error("Failed to delete project:", e);
      }
    },
    [communityId]
  );

  const projectById = useMemo(
    () => new Map(projects.map((p) => [p.id, p])),
    [projects]
  );

  return { projects, loading, projectById, remove };
};

export default useMediaProjects;
